import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { shipmentStorageService } from '../../../services/shipmentStorageService';
import { supabase } from '../../../lib/supabase';

const PhotoUploadSection = ({ shipmentId, currentPhotoUrl, photosUrls = [], onPhotoUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadingExtra, setUploadingExtra] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [extraPhotos, setExtraPhotos] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  const mainPhotoInputRef = useRef(null);
  const extraPhotoInputRef = useRef(null);

  useEffect(() => {
    loadPhotos();
  }, [currentPhotoUrl, photosUrls]);

  const loadPhotos = async () => {
    try {
      // Load main photo
      if (currentPhotoUrl) {
        const url = await shipmentStorageService?.getPhotoUrl(currentPhotoUrl);
        setImageUrl(url);
      }
      
      // Load extra photos
      if (Array.isArray(photosUrls) && photosUrls?.length > 0) {
        const urlPromises = photosUrls?.map(async (path) => {
          const url = await shipmentStorageService?.getPhotoUrl(path);
          return { path, url };
        });
        const photos = await Promise.all(urlPromises);
        setExtraPhotos(photos?.filter(p => p?.url !== null));
      }
    } catch (error) {
      console.error('Error loading photos:', error);
    }
  };

  const handleMainPhotoChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes?.includes(file?.type)) {
      alert('Format non supporté. Veuillez choisir une image JPG, PNG ou WEBP.');
      return;
    }

    // Validate file size (10MB max)
    if (file?.size > 10 * 1024 * 1024) {
      alert('Le fichier est trop volumineux. Taille maximale: 10MB');
      return;
    }

    try {
      setUploading(true);
      
      // Upload to storage
      const filePath = await shipmentStorageService?.uploadPhoto(file, shipmentId);
      
      // Get signed URL
      const signedUrl = await shipmentStorageService?.getPhotoUrl(filePath);
      
      // Update shipments table with photo_url
      const { error: updateError } = await supabase
        ?.from('shipments')
        ?.update({ photo_url: filePath })
        ?.eq('id', shipmentId);

      if (updateError) throw updateError;

      // ✅ Insert into shipment_files with file_type='photo'
      await shipmentStorageService?.savePhotoMetadata(shipmentId, filePath, file?.name, file?.size);

      // Update local state
      setImageUrl(signedUrl);
      
      alert('Photo principale téléversée avec succès!');
      
      if (onPhotoUploaded) {
        onPhotoUploaded();
      }
    } catch (error) {
      console.error('Error uploading main photo:', error);
      alert('Erreur lors du téléversement: ' + error?.message);
    } finally {
      setUploading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleExtraPhotoChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes?.includes(file?.type)) {
      alert('Format non supporté. Veuillez choisir une image JPG, PNG ou WEBP.');
      return;
    }

    // Validate file size (10MB max)
    if (file?.size > 10 * 1024 * 1024) {
      alert('Le fichier est trop volumineux. Taille maximale: 10MB');
      return;
    }

    try {
      setUploadingExtra(true);
      
      // Upload to storage
      const filePath = await shipmentStorageService?.uploadPhoto(file, shipmentId);
      
      // Get current photos_urls array
      const { data: shipment, error: fetchError } = await supabase
        ?.from('shipments')
        ?.select('photos_urls')
        ?.eq('id', shipmentId)
        ?.single();

      if (fetchError) throw fetchError;

      // Add new photo URL to array
      const currentPhotos = Array.isArray(shipment?.photos_urls) ? shipment?.photos_urls : [];
      const updatedPhotos = [...currentPhotos, filePath];
      
      // Update shipments table with new photos array
      const { error: updateError } = await supabase
        ?.from('shipments')
        ?.update({ photos_urls: updatedPhotos })
        ?.eq('id', shipmentId);

      if (updateError) throw updateError;

      // ✅ Insert into shipment_files with file_type='photo'
      await shipmentStorageService?.savePhotoMetadata(shipmentId, filePath, file?.name, file?.size);

      alert('Photo supplémentaire téléversée avec succès!');
      
      // Reload photos
      await loadPhotos();
      
      if (onPhotoUploaded) {
        onPhotoUploaded();
      }
    } catch (error) {
      console.error('Error uploading extra photo:', error);
      alert('Erreur lors du téléversement: ' + error?.message);
    } finally {
      setUploadingExtra(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleDeleteExtraPhoto = async (photoPath) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette photo?')) return;

    try {
      // Delete from storage
      await shipmentStorageService?.deletePhoto(photoPath);
      
      // Get current photos_urls array
      const { data: shipment, error: fetchError } = await supabase
        ?.from('shipments')
        ?.select('photos_urls')
        ?.eq('id', shipmentId)
        ?.single();

      if (fetchError) throw fetchError;

      // Remove photo from array
      const currentPhotos = Array.isArray(shipment?.photos_urls) ? shipment?.photos_urls : [];
      const updatedPhotos = currentPhotos?.filter(p => p !== photoPath);
      
      // Update shipments table
      const { error: updateError } = await supabase
        ?.from('shipments')
        ?.update({ photos_urls: updatedPhotos })
        ?.eq('id', shipmentId);

      if (updateError) throw updateError;

      alert('Photo supprimée avec succès!');
      
      // Reload photos
      await loadPhotos();
      
      if (onPhotoUploaded) {
        onPhotoUploaded();
      }
    } catch (error) {
      console.error('Error deleting extra photo:', error);
      alert('Erreur lors de la suppression: ' + error?.message);
    }
  };

  const openPreview = (url) => {
    setPreviewUrl(url);
    setShowPreview(true);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Camera" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Photos du colis</h3>
            <p className="text-sm text-muted-foreground">Photo principale et photos supplémentaires</p>
          </div>
        </div>
      </div>

      {/* Main Photo Section */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
        <div className="flex items-center space-x-2 mb-2">
          <Icon name="Image" size={18} className="text-blue-600" />
          <h4 className="text-sm font-semibold text-blue-900">Photo principale</h4>
        </div>

        {imageUrl && (
          <div className="mb-4">
            <div 
              className="relative cursor-pointer group overflow-hidden rounded-lg border border-border"
              onClick={() => openPreview(imageUrl)}
            >
              <img
                src={imageUrl}
                alt="Photo actuelle"
                className="w-full h-48 object-cover"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Icon name="ZoomIn" size={32} className="text-white" />
              </div>
            </div>
          </div>
        )}

        <input
          type="file"
          ref={mainPhotoInputRef}
          accept="image/jpeg,image/png,image/webp"
          onChange={handleMainPhotoChange}
          disabled={uploading}
          className="hidden"
        />
        <label htmlFor="main-photo-upload" className="block cursor-pointer">
          <Button
            as="span"
            variant="outline"
            fullWidth
            iconName={uploading ? 'Loader' : 'Upload'}
            iconPosition="left"
            disabled={uploading}
            className="cursor-pointer hover:bg-primary/10 transition-colors"
            onClick={() => mainPhotoInputRef?.current?.click()}
          >
            {uploading ? 'Téléversement en cours...' : imageUrl ? 'Remplacer la photo principale' : 'Téléverser la photo principale'}
          </Button>
        </label>
      </div>

      {/* Extra Photos Section */}
      <div className="mb-6 p-4 bg-muted/30 rounded-lg space-y-3">
        <div className="flex items-center space-x-2 mb-2">
          <Icon name="Images" size={18} className="text-gray-600" />
          <h4 className="text-sm font-semibold text-gray-900">Photos supplémentaires</h4>
        </div>

        {/* Extra Photos Grid */}
        {extraPhotos?.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            {extraPhotos?.map((photo, index) => (
              <div key={index} className="relative group">
                <div 
                  className="cursor-pointer overflow-hidden rounded-lg border border-border"
                  onClick={() => openPreview(photo?.url)}
                >
                  <img
                    src={photo?.url}
                    alt={`Photo supplémentaire ${index + 1}`}
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Icon name="ZoomIn" size={24} className="text-white" />
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteExtraPhoto(photo?.path)}
                  className="absolute top-2 right-2 w-6 h-6 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Icon name="X" size={14} className="text-white" />
                </button>
              </div>
            ))}
          </div>
        )}

        <input
          type="file"
          ref={extraPhotoInputRef}
          accept="image/jpeg,image/png,image/webp"
          onChange={handleExtraPhotoChange}
          disabled={uploadingExtra}
          className="hidden"
        />
        <label htmlFor="extra-photo-upload" className="block cursor-pointer">
          <Button
            as="span"
            variant="outline"
            fullWidth
            iconName={uploadingExtra ? 'Loader' : 'Upload'}
            iconPosition="left"
            disabled={uploadingExtra}
            className="cursor-pointer hover:bg-primary/10 transition-colors"
            onClick={() => extraPhotoInputRef?.current?.click()}
          >
            {uploadingExtra ? 'Téléversement en cours...' : 'Téléverser une photo'}
          </Button>
        </label>

        <p className="text-xs text-muted-foreground text-center">
          Formats acceptés: JPG, PNG, WEBP (max 10MB)
        </p>
      </div>

      {/* Preview Modal */}
      {showPreview && previewUrl && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowPreview(false)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300"
            onClick={() => setShowPreview(false)}
          >
            <Icon name="X" size={32} />
          </button>
          <img
            src={previewUrl}
            alt="Aperçu photo"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
    </div>
  );
};

export default PhotoUploadSection;